import { ensureCsrfTokenResult, resetCsrfToken } from '../utilities/csrf.js';

const CAPTCHA_TIMEOUT_MS = 10000;
const CAPTCHA_FIELD_ID = 'partner_review_id';
const CAPTCHA_CLIENT_ID = `client${CAPTCHA_FIELD_ID}`;
const REQUEST_FAILURE_EVENT = 'reviews:livewire-request-failed';

export default (messages = {}) => ({
    open: false,
    busy: false,
    failure: false,
    failureMessage: '',
    defaultFailureMessage: messages.failureMessage || '',
    ratingError: false,
    ratingErrorMessage: messages.ratingErrorMessage || '',
    rating: 0,
    hover: 0,
    returnFocus: null,

    openModal(event) {
        this.returnFocus = event.currentTarget;
        this.open = true;
        this.failure = false;
        this.failureMessage = '';
        this.ratingError = false;
        this.rating = 0;
        this.hover = 0;
        this.$wire.set('rating', 0, false);
        this.$nextTick(() => this.$refs.name?.focus());
    },

    closeModal() {
        if (!this.open || this.busy) {
            return;
        }

        this.open = false;
        this.$nextTick(() => this.returnFocus?.focus());
    },

    trapFocus(event) {
        if (event.key !== 'Tab') {
            return;
        }

        const focusable = [...this.$refs.dialog.querySelectorAll(
            'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )].filter((element) => element.offsetParent !== null);

        if (focusable.length === 0) {
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    },

    handleRatingKey(event, current) {
        let next = current;

        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
            next = Math.min(5, current + 1);
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
            next = Math.max(1, current - 1);
        } else if (event.key === 'Home') {
            next = 1;
        } else if (event.key === 'End') {
            next = 5;
        } else {
            return;
        }

        event.preventDefault();
        this.selectRating(next);
        event.currentTarget
            .closest('.review-modal__stars')
            ?.querySelectorAll('.review-modal__star')[next - 1]
            ?.focus();
    },

    selectRating(rating) {
        this.rating = rating;
        this.ratingError = false;
        this.$wire.set('rating', rating, false);
    },

    async send() {
        if (this.busy || this.$wire.submitted) {
            return;
        }

        if (!Number.isInteger(this.rating) || this.rating < 1 || this.rating > 5) {
            this.ratingError = true;
            this.$nextTick(() => this.$refs.rating?.querySelector('.review-modal__star:not(:disabled)')?.focus());

            return;
        }

        this.ratingError = false;

        this.busy = true;
        this.failure = false;
        this.failureMessage = '';

        try {
            let csrf = await ensureCsrfTokenResult();
            if (!csrf.token) {
                this.showFailure();
                return;
            }

            let captchaToken = await this.getCaptchaToken();
            if (!captchaToken) {
                this.showFailure();
                return;
            }

            let result = await this.submitToLivewire(captchaToken);

            if (result.status === 419) {
                resetCsrfToken();
                csrf = await ensureCsrfTokenResult();
                if (!csrf.token) {
                    this.showFailure();
                    return;
                }

                captchaToken = await this.getCaptchaToken();
                if (!captchaToken) {
                    this.showFailure();
                    return;
                }

                result = await this.submitToLivewire(captchaToken);
            }

            if (result.kind !== 'complete') {
                this.showFailure();
            }
        } catch {
            this.showFailure();
        } finally {
            this.busy = false;
        }
    },

    submitToLivewire(captchaToken) {
        return new Promise((resolve) => {
            const eventTarget = this.$wire?.$el;
            if (!eventTarget?.addEventListener || !eventTarget?.removeEventListener) {
                resolve({ kind: 'failure' });
                return;
            }

            let settled = false;
            const finish = (result) => {
                if (settled) {
                    return;
                }

                settled = true;
                eventTarget?.removeEventListener(REQUEST_FAILURE_EVENT, onFailure);
                resolve(result);
            };
            const onFailure = (event) => finish({ kind: 'failure', status: event.detail?.status });

            eventTarget.addEventListener(REQUEST_FAILURE_EVENT, onFailure, { once: true });

            try {
                Promise.resolve(this.$wire.submit(captchaToken)).then(
                    () => finish({ kind: 'complete' }),
                    () => finish({ kind: 'failure' })
                );
            } catch {
                finish({ kind: 'failure' });
            }
        });
    },

    showFailure() {
        this.failureMessage = this.defaultFailureMessage;
        this.failure = true;
    },

    async getCaptchaToken() {
        const captcha = window.grecaptcha;
        if (typeof captcha?.ready !== 'function' || typeof captcha?.execute !== 'function') {
            return '';
        }

        return new Promise((resolve, reject) => {
            let finished = false;
            const finish = (callback, value) => {
                if (finished) {
                    return;
                }
                finished = true;
                window.clearTimeout(timeoutId);
                callback(value);
            };
            const timeoutId = window.setTimeout(() => {
                finish(reject, new Error('Review reCAPTCHA timed out.'));
            }, CAPTCHA_TIMEOUT_MS);

            try {
                captcha.ready(() => {
                    if (finished) {
                        return;
                    }
                    try {
                        const clientId = this.getCaptchaClient(captcha);
                        Promise.resolve(captcha.execute(clientId, { action: 'partner_review' }))
                            .then((token) => finish(resolve, token), (error) => finish(reject, error));
                    } catch (error) {
                        finish(reject, error);
                    }
                });
            } catch (error) {
                finish(reject, error);
            }
        });
    },

    getCaptchaClient(captcha) {
        const existingClientId = window[CAPTCHA_CLIENT_ID];
        if (existingClientId !== undefined && existingClientId !== null) {
            return existingClientId;
        }

        const field = document.getElementById(CAPTCHA_FIELD_ID);
        const container = field?.closest('[data-review-captcha-site-key]');
        const siteKey = container?.dataset.reviewCaptchaSiteKey;

        if (!field || !siteKey || typeof captcha.render !== 'function') {
            throw new Error('Review reCAPTCHA widget is unavailable.');
        }

        const options = {
            sitekey: siteKey,
            size: 'invisible',
            hl: container.dataset.reviewCaptchaLanguage || 'ru',
        };

        if (container.dataset.reviewCaptchaInline === 'true') {
            options.badge = 'inline';
        }

        const clientId = captcha.render(field, options);
        window[CAPTCHA_CLIENT_ID] = clientId;

        return clientId;
    },
});
